import { ForwardedRef, forwardRef, useEffect, useImperativeHandle, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box } from '@mui/material';
import { DataGrid, gridClasses, GridFilterModel, GridPaginationModel, GridSortModel } from '@mui/x-data-grid';

import { tableStyles } from '@shared/styles';
import { PaginationArgs } from '@shared/types';

import { AppDispatch } from '@store';

import { ServerDataGridPublicMethods, ServerDataGridProps } from './types';

const { tableContainer } = tableStyles;

export const ServerDataGridInner = <T,>(
  {
    pageSelector,
    setPageAction,
    totalCountSelector,
    setTotalCountAction,
    listSelector,
    setListAction,
    sort,
    setSort,
    filter,
    setFilter,
    limit,
    useLazyListQuery,
    ...props
  }: ServerDataGridProps<T>,
  ref: ForwardedRef<ServerDataGridPublicMethods>
) => {
  const dispatch = useDispatch<AppDispatch>();

  const page = useSelector(pageSelector);
  const totalCount = useSelector(totalCountSelector);
  const list = useSelector(listSelector);

  const paginationModel = useMemo(() => {
    return { page: page - 1, pageSize: limit };
  }, [page, limit]);

  const [getList, { isLoading }] = useLazyListQuery();

  const fetchList = async (page: number) => {
    const { field: sortField, sort: sortOrder } = sort[0];

    const data = await getList({
      limit,
      page,
      sortFields: {
        [sortField]: sortOrder,
      },
      filter: JSON.stringify(filter),
    } as PaginationArgs<T>).unwrap();

    dispatch(setListAction(data.result));

    return data;
  };

  useEffect(() => {
    const getData = async () => {
      const currentPage = 1;

      const { total } = await fetchList(currentPage);

      dispatch(setPageAction(currentPage));
      dispatch(setTotalCountAction(total));
    };

    getData();
  }, [filter, sort]);

  useEffect(() => {
    const getData = async () => {
      await fetchList(page);
    };

    getData();
  }, [page]);

  const handleSortChange = (newSort: GridSortModel) => {
    setSort(newSort);
  };

  const handleFilterChange = (newFilterModel: GridFilterModel) => {
    const { value, field } = newFilterModel.items[0];

    setFilter(value ? { [field]: value } : {});
  };

  const handlePaginationModelChange = (model: GridPaginationModel) => {
    dispatch(setPageAction(model.page + 1));
  };

  // Public methods
  const refreshList = async () => {
    await fetchList(page);
  };

  useImperativeHandle(ref, () => ({
    refreshList,
  }));

  return (
    <>
      <Box className={tableContainer}>
        <DataGrid
          filterMode="server"
          sortingMode="server"
          paginationMode="server"
          sortingOrder={['asc', 'desc']}
          rows={list}
          rowCount={totalCount}
          pageSizeOptions={[limit]}
          loading={isLoading}
          paginationModel={paginationModel}
          onPaginationModelChange={handlePaginationModelChange}
          sortModel={sort}
          onSortModelChange={handleSortChange}
          onFilterModelChange={handleFilterChange}
          getRowHeight={() => 'auto'}
          pagination
          // components={{
          //   LoadingOverlay: () => <BaseSpinner type="linear" />,
          // }}
          sx={{
            [`& .${gridClasses.cell}`]: {
              py: 1,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-all',
            },
          }}
          {...props}
        />
      </Box>
    </>
  );
};

export const ServerDataGrid = forwardRef(ServerDataGridInner) as <T>(
  props: ServerDataGridProps<T> & {
    ref?: ForwardedRef<ServerDataGridPublicMethods>;
  }
) => ReturnType<typeof ServerDataGridInner>;
